import * as React from 'react';
import { Dialog, AppBar, DialogActions, Button, WithStyles, createStyles, Theme, withStyles, DialogContent, Typography, Toolbar, FormControl, InputLabel, InputBase, Grid, Input, Divider } from '@material-ui/core';
import { DeltaXMLColors, MyDeltaColors } from './styling/DeltaColors';
import { Utilities } from '../general/utilities';
import { ProductId } from './NavigatorData';
import FileUploader from './FileUploader';
import FileInput, { InputIdentifier } from './FileInput';
import { AuthContext } from '../authentication/AuthContext';
import FormDataType, { BaseConfiguration, InputFilenameAId, InputFilenameBId, ResultFilename } from './ConfigFormData';
import GetAppIcon from '@material-ui/icons/GetApp';
import { MenuItemFormatType } from './RequestEvaluation';
import Select from 'react-select';
import { TrialProducts } from './Paperbase';
import { CircularProgress } from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DonutLargeIcon from '@material-ui/icons/DonutLarge';
import { MuiStyles } from './styling/MuiStyles';

const styles = (theme: Theme) =>
  createStyles({
    inputLabel: {
      fontSize: '17',
      width: '150px',
      color: DeltaXMLColors.OXFORD_BLUE
    },
    checkIcon: {
      color: DeltaXMLColors.CELERY
    },
    fileButton: {
      display: 'inline-flex',
      marginEnd: '4px',
      backgroundColor: 'transparent',
      border: '1px solid',
      color: DeltaXMLColors.OXFORD_BLUE,
      padding: '5px 10px',
      marginStart: '0px',
      '&:hover': {
        // Set hover color
        backgroundColor: DeltaXMLColors.OXFORD_BLUE,
        color: MyDeltaColors.WHITE,
        border: '1px solid ' + DeltaXMLColors.OXFORD_BLUE,
        boxShadow: 'none'
      },
      width: '131px',
      height: '32px'
    },
    resultFileIcon: {
      color: DeltaXMLColors.FOUNTAIN_BLUE
    },
    fileLabel: {
      marginStart: '1em',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      width: '300px',
      overflow: 'hidden'
    },
    errorGridItem: {
      paddingTop: '9px'
    },
    quickCompareError: {
      color: DeltaXMLColors.VALENTINE_RED,
      backgroundColor: MyDeltaColors.ERROR_RED,
      padding: "10px",
      margin: "5px"
    },
    dropDown: {
      paddingTop: '5px',
      height: '41px',
      width: '240px',
    },
    errorIcon: {
      color: DeltaXMLColors.VALENTINE_RED
    },
    divider: {
      backgroundColor: MyDeltaColors.MERCURY,
      margin: '4px 0px 4px 0px'
    },
    dialog: {
      marginTop: '20px'
    },
    hidden: {
      display: "none"
    },
    donutIcon: {
      color: DeltaXMLColors.OXFORD_BLUE
    },
    link: {
      color: DeltaXMLColors.FOUNTAIN_BLUE
    }
  });

export enum OutputType {
  HTML = 'html',
  S1000D = 's1000d',
  XSLFO = 'xsl-fo',
  OXYGENTC = 'oxygen-tc',
  ARBORTEXTTC = 'arbortext-tc',
  XMETALTC = 'xmetal-tc',
  FRAMEMAKERTC = 'framemaker-tc'
}

export interface CCProdOutputDataType {
  [ProductId.ccS1000D]: MenuItemFormatType[],
  [ProductId.ccJATS]: MenuItemFormatType[],
  [ProductId.ccBITS]: MenuItemFormatType[],
  [ProductId.ccNISOSTS]: MenuItemFormatType[],
  [ProductId.ccXSLFO]: MenuItemFormatType[]
}

const commonCCOutputTypes: MenuItemFormatType[] = [
  { label: OutputType.OXYGENTC, value: OutputType.OXYGENTC },
  { label: OutputType.ARBORTEXTTC, value: OutputType.ARBORTEXTTC },
  { label: OutputType.XMETALTC, value: OutputType.XMETALTC },
  { label: OutputType.FRAMEMAKERTC, value: OutputType.FRAMEMAKERTC }
]

const ccProdOutputTypes: CCProdOutputDataType = {
  [ProductId.ccS1000D]: commonCCOutputTypes.concat({ label: OutputType.S1000D, value: OutputType.S1000D }),
  [ProductId.ccJATS]: commonCCOutputTypes.concat({ label: OutputType.HTML, value: OutputType.HTML }),
  [ProductId.ccBITS]: commonCCOutputTypes.concat({ label: OutputType.HTML, value: OutputType.HTML }),
  [ProductId.ccNISOSTS]: commonCCOutputTypes.concat({ label: OutputType.HTML, value: OutputType.HTML }),
  [ProductId.ccXSLFO]: commonCCOutputTypes.concat({ label: OutputType.XSLFO, value: OutputType.XSLFO }),
}

export enum CCStatus {
  NotYetRequested,
  RequestOngoing,
  Success,
  Error,
  InternalServerError
}
export interface QuickCompareProps extends WithStyles<typeof styles> {
  open: boolean,
  handleQuickCompareDialogOpen: (open: boolean) => void,
  productId: TrialProducts
}

function QuickCompare(props: QuickCompareProps) {
  const authContext = React.useContext(AuthContext);
  const { classes, open, handleQuickCompareDialogOpen, productId } = props;
  const initialFormValues = FormDataType.defaults;
  const [allFormValues, setFormValues] = React.useState(initialFormValues);
  const configFormValues = allFormValues[productId];
  const [resultStore, setResultStore] = React.useState<any>('');
  const [selectedOutputType, setSelectedOutputType] = React.useState<MenuItemFormatType | null>({ label: OutputType.OXYGENTC, value: OutputType.OXYGENTC });
  const allowedCCOutputTypes = ccProdOutputTypes[productId];
  const [ccRequestStatus, setCCRequestStatus] = React.useState<CCStatus>(CCStatus.NotYetRequested)
  const [fileStateA, setfileStateA] = React.useState<string>(Utilities.emptyFile)
  const [fileStateB, setfileStateB] = React.useState<string>(Utilities.emptyFile)
  const [serverErrorStore, setServerErrorStore] = React.useState<string | null>(null)
  const [resultFilename, setResultFilename] = React.useState(configFormValues.ResultFilename);

  const handleResultFilenameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setResultFilename(event.target.value);
  };

  const onFormDrop = (event: React.DragEvent<HTMLInputElement>, inputIdentifier: InputIdentifier) => {
    event.preventDefault();
    const files = event.dataTransfer.files;
    if (files.length === 1) {
      const file = files[0];
      const updatedFormValues: BaseConfiguration = { ...configFormValues };

      if (inputIdentifier === InputIdentifier.A) {
        updatedFormValues.InputFilenameA = file.name;
        updatedFormValues.InputFileA = file;
        setfileStateA(Utilities.fileUploadSuccessful);
      } else if (inputIdentifier === InputIdentifier.B) {
        updatedFormValues.InputFilenameB = file.name;
        updatedFormValues.InputFileB = file;
        setfileStateB(Utilities.fileUploadSuccessful);
      }
      // Update the form values
      setFormValues({
        ...allFormValues,
        [productId]: updatedFormValues,
      });
    }
  };
  const onFormChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    const file = event.target.files![0];
    const fileName = file.name;
    const inputName = event.target.getAttribute('name');
    const updatedFormValues: BaseConfiguration = {
      ...configFormValues,
    };
    if (inputName === InputFilenameAId) {
      updatedFormValues.InputFilenameA = fileName;
      updatedFormValues.InputFileA = file;
      setfileStateA(Utilities.fileUploadSuccessful)
    }
    if (inputName === InputFilenameBId) {
      updatedFormValues.InputFilenameB = fileName;
      updatedFormValues.InputFileB = file;
      setfileStateB(Utilities.fileUploadSuccessful)
    }

    // Update the form values
    setFormValues({
      ...allFormValues,
      [productId]: updatedFormValues,
    });


  };

  React.useEffect(() => {
    const fileName = resultFilename.substring(0, resultFilename.indexOf('.'));
    if (selectedOutputType?.label === OutputType.HTML) {
      const fileNameHtml = fileName + ".html";
      setResultFilename(fileNameHtml);
    } else {
      const filenameXml = fileName + ".xml";
      setResultFilename(filenameXml);
    }
  }, [selectedOutputType]);

  const download = () => {
    const a = window.document.createElement('a');
    a.setAttribute('href', `data:application/xml;,${encodeURIComponent(resultStore)}`);
    a.style.display = 'none';
    a.setAttribute('download', resultFilename);
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const convertFileToBlob = (file: File): Blob | null => {
    const blob: Blob | null = file.slice(0, file.size, "application/xml" || "text/xml");
    return blob;
  };

  const contentCompareUrl = process.env.REACT_APP_CC_API_URL;

  const getComparisonResults = async () => {
    setCCRequestStatus(CCStatus.RequestOngoing);

    const formData = new FormData()
    if (configFormValues.InputFileA !== null) {
      const blobInputA = convertFileToBlob(configFormValues.InputFileA);
      if (blobInputA !== null) {
        formData.append("inputA", blobInputA, configFormValues.InputFileA.name);
      }
    }
    if (configFormValues.InputFileB !== null) {
      const blobInputB = convertFileToBlob(configFormValues.InputFileB);
      if (blobInputB !== null) {
        formData.append("inputB", blobInputB,
          configFormValues.InputFileB.name);
      }
    }
    formData.append("outputType", selectedOutputType ? selectedOutputType.value : '');

    const idToken = authContext.authVariables?.id_token;
    const getRequestOptions: RequestInit = {
      method: 'POST',
      headers: {
        "Authorization": "Bearer " + idToken,
        "Accept": "application/xml",
        "DX-User-Agent": "dxml-mydelta-management-app",
      },
      body: formData
    };

    const productIdParts = productId.split(" ");
    const productUrlPart = encodeURIComponent(productIdParts[productIdParts.length - 1].replace("-", ""));
    const compareUrl = contentCompareUrl + "/" + productUrlPart + "/compare";

    try {
      const response = await fetch(compareUrl, getRequestOptions);
      if (response.ok) {
        const dealObject: any = await response.text();
        setCCRequestStatus(CCStatus.Success);
        setResultStore(dealObject);
      } else {
        const errorResponse = await response.text();
        const startIndex = (errorResponse).indexOf('<errorMessage>');
        const endIndex = (errorResponse).indexOf('</errorMessage>');
        setServerErrorStore(errorResponse.substring(startIndex + 14, endIndex));
        setCCRequestStatus(CCStatus.Error);
      }
    } catch (err) {
      setCCRequestStatus(CCStatus.InternalServerError);
    }
  }
  const resultEnable = ccRequestStatus === CCStatus.Success;
  const requestInProgress = ccRequestStatus === CCStatus.RequestOngoing;
  const runCompareEnabled = !requestInProgress && (fileStateA === Utilities.fileUploadSuccessful) && (fileStateB === Utilities.fileUploadSuccessful);
  const showError = (ccRequestStatus === CCStatus.InternalServerError || ccRequestStatus === CCStatus.Error) && !requestInProgress && !resultEnable;
  const errorMessage = ccRequestStatus === CCStatus.InternalServerError ?
  <Typography variant="body1">Unable to perform comparison right now. Please contact <a className={classes.link} href="mailto:support@deltaxml.com?subject=Error performing Content Compare comparison.">support@deltaxml.com</a> if the problem persists.</Typography>
  : serverErrorStore;

  return (
    <Dialog open={open} onClose={() => handleQuickCompareDialogOpen(false)} PaperProps={{ style: { height: '600px' } }}>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h4">{productId} Comparison</Typography>
        </Toolbar>
      </AppBar>
      <DialogContent className={classes.dialog}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FileUploader onFormDrop={(event) => onFormDrop(event, InputIdentifier.A)} formValues={configFormValues} inputIdentifier={InputIdentifier.A}>
              <FileInput onFormChange={onFormChange} inputFilename={configFormValues.InputFilenameA}
                inputFile={configFormValues.InputFileA}
                inputIdentifier={InputIdentifier.A}
                errorMessage={fileStateA} />
            </FileUploader>
            <FileUploader onFormDrop={(event) => onFormDrop(event, InputIdentifier.B)} formValues={configFormValues} inputIdentifier={InputIdentifier.B} >
              <FileInput onFormChange={onFormChange} inputFilename={configFormValues.InputFilenameB}
                inputFile={configFormValues.InputFileB}
                inputIdentifier={InputIdentifier.B}
                data-input-identifier={InputIdentifier.B}
                errorMessage={fileStateB} />
            </FileUploader>
          </Grid>
          <Grid item xs={12}>
            <Grid container>
              <Grid item xs={11}>
                <Button className={classes.fileButton} endIcon={<GetAppIcon className={classes.resultFileIcon} />} onClick={download} disabled={!resultEnable}>Result File</Button>
                <Input spellCheck={false} className={classes.fileLabel} id="fileResult" name={ResultFilename} value={resultFilename} onChange={handleResultFilenameChange} />
              </Grid>
              <Grid item xs={1} className={classes.errorGridItem}>
                {requestInProgress ? <CircularProgress size={24} />
                  : ccRequestStatus === CCStatus.Success ? <CheckCircleIcon className={classes.checkIcon} />
                    : <MuiStyles.HtmlTooltip arrow title={<Typography color="inherit" variant="body2">Operation not yet run</Typography>}>
                      <DonutLargeIcon className={classes.donutIcon} />
                    </MuiStyles.HtmlTooltip>
                }
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>
          <Grid item xs={12}>
            <FormControl>
              <InputLabel className={classes.inputLabel} shrink={true}>
                Output Type
              </InputLabel>
            </FormControl>
            <Select className={classes.dropDown} name="outputType" value={selectedOutputType} onChange={product => { setSelectedOutputType(product) }} options={allowedCCOutputTypes} isSearchable={false} isDisabled={false} />
          </Grid>
          <Grid item xs={12}>
            <Typography className={showError ? classes.quickCompareError : classes.hidden} variant="body1">{errorMessage}</Typography>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button variant="contained" color="primary" onClick={getComparisonResults} disabled={!runCompareEnabled}>
          Run
        </Button>
        <Button variant="contained" color="primary" onClick={() => handleQuickCompareDialogOpen(false)} disabled={requestInProgress}>
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
};
export default withStyles(styles)(QuickCompare);